/**
 * The one shutter every collapsible region in the kit opens through.
 *
 * It exists because the markup was copied. `CresultcardDisclosure` sets
 * `inert` on its pane while collapsed; `CresultcardActionGroup` reproduced the
 * same two divs and the same two class names and did not — so three invisible
 * alternates sat in the tab order behind a control that said `aria-expanded:
 * false`. A second copy of a structure is a second chance to forget half of it,
 * and there are fifteen more families queued behind this one.
 *
 * Content is ALWAYS mounted and `grid-template-rows: 0fr → 1fr` animates, so
 * retraction is the same duration and the same curve as expansion. While
 * collapsed the pane is `inert`: out of the tab order and out of the
 * accessibility tree without being unmounted, which is what lets the retraction
 * animate at all.
 */
"use client";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import "./Cresultcard.css";

const SHUTTER_CLASS_NAME = "cresultcard__disclosure-shutter";
const PANE_CLASS_NAME = "cresultcard__disclosure-pane";

interface CresultcardShutterProps {
  readonly open: boolean;
  /** The id the control that governs this region points `aria-controls` at. */
  readonly id: string;
  readonly children: ReactNode;
  /** The edge the region grows FROM, when it is not the full width of its holder. */
  readonly transformOrigin?: CSSProperties["transformOrigin"];
}

export function CresultcardShutter({
  open,
  id,
  children,
  transformOrigin,
}: CresultcardShutterProps): ReactElement {
  return (
    <div
      className={SHUTTER_CLASS_NAME}
      data-el="Cresultcard/Shutter/Root"
      data-crc-expanded={open}
      style={transformOrigin == null ? undefined : { transformOrigin }}
    >
      <div
        className={PANE_CLASS_NAME}
        data-el="Cresultcard/Shutter/Panel"
        id={id}
        inert={!open}
      >
        {children}
      </div>
    </div>
  );
}
